/**
 * Row / item status — the shared "tint this row" primitive.
 *
 * Components never take a colour from the host; they take a *status* and map it
 * to a class themselves. That is deliberate: apps switch DaisyUI themes, so an
 * absolute colour (`bg-red-50`) is wrong the moment the theme changes. Making
 * the status a closed set means a caller physically cannot say the wrong thing.
 *
 * The spec shapes mirror `resolveColor()` in g-card-grid (colorMap + colorRule),
 * which is already the documented way to colour a badge — this is the same idea
 * one level up, at the row.
 *
 *   // callback
 *   el.rowStatus = (row) => row.remaining < 0 ? 'error' : null;
 *
 *   // declarative, strict equality
 *   { field: 'budgetStatus', colorMap: { 'Over Budget': 'error', 'On Budget': 'success' } }
 *
 *   // declarative, predicates — first match wins, a trailing rule with no
 *   // `when` is the catch-all. This form is the one that matters: a threshold
 *   // ("over budget" = remaining < 0) cannot be expressed as an equality map
 *   // without inventing a precomputed status field on the record.
 *   { field: 'remaining', colorRule: [{ when: { lt: 0 }, status: 'error' }] }
 *
 *   // no `field` → the predicate is evaluated against the whole record, so it
 *   // can reference several fields ({ all }, { any }, { and }, { or }).
 *   { colorRule: [{ when: { all: { stage: { eq: 'Open' }, days: { gt: 30 } } }, status: 'warning' }] }
 */
import { evalPredicate, evalRecord } from './predicate.js';
import { HUE_TOKENS, ensureHueStyles } from './hues.js';

// Tint-capable semantic tokens only.
//
// `ghost` is deliberately absent even though g-card-grid's badge resolver
// allows it: DaisyUI has no `bg-ghost`, so accepting it here would emit a class
// that renders nothing — a status that silently does nothing is worse than a
// rejected one.
export const STATUS_TOKENS = new Set([
  'primary', 'secondary', 'accent', 'info', 'success', 'warning', 'error', 'neutral',
  // Theme hues (util/hues.js): derived from the theme, so they follow it too.
  ...HUE_TOKENS,
]);

// ROW ACTIONS appear on hover, because a button on every row of a long table is
// a wall of icons competing with the data you came to read.
//
// Two things keep that from becoming an accessibility problem. The whole rule
// sits inside `@media (hover:hover)`, so a touch device — which has no hover to
// reveal them with — gets them permanently visible instead. And `:focus-within`
// reveals them for keyboard users, who would otherwise tab into a control they
// cannot see. `opacity` rather than `display`, so the column keeps its width and
// rows do not reflow as the pointer moves down the table.
//
// SELECTION is a separate channel from STATUS, on purpose.
//
// "This row is the one you picked" and "this row is over budget" are different
// facts, and a row can be both. Expressing selection by writing a status token
// would silently overwrite whatever the view was using the status channel to
// say — and the first attempt did exactly that, pinning every selected row to
// `info` and clobbering any real row status underneath it.
//
// It takes the theme's PRIMARY rather than a fixed semantic, so the highlight
// is whatever that app's accent is instead of blue everywhere, and it adds an
// inset left bar so selection does not rely on colour alone.
//
// ── Why this ships its own CSS instead of emitting Tailwind classes ──
//
// The obvious implementation is `bg-error/10 hover:bg-error/20`. It does not
// work in this stack, and the reason is worth writing down because it is not
// the usual "JIT can't see dynamic classes" story:
//
//   * Host apps load `@tailwindcss/browser` + a separate daisyUI <link>.
//     Tailwind compiles from the live DOM, so runtime-injected classes ARE
//     picked up — that part is fine.
//   * But Tailwind never ingests daisyUI's stylesheet, so it does not know
//     `error` / `base-200` are colours and cannot generate ANY `bg-{token}`
//     utility. Inspecting Tailwind's runtime output finds zero of them.
//   * daisyUI ships the base utilities itself (`.bg-error/10` is in its CDN
//     build) — which is why a plain tint appears to work — but it ships no
//     `hover:` colour variants at all.
//
// Net: `bg-error/10` paints, `hover:bg-error/20` silently does nothing. Since
// a tint that kills the row's hover response is worse than no tint, the hover
// half cannot be optional. Owning ~12 lines of CSS keyed on a data attribute
// removes the dependency entirely, works with no host cooperation, and stays
// correct if a host ever switches to a build-time Tailwind.
const STYLE_ID = 'g-status-styles';

const TINT_CSS = `
[data-g-status]{--g-tint:var(--color-neutral);background-color:color-mix(in oklab,var(--g-tint) 10%,transparent)}
[data-g-status="primary"]{--g-tint:var(--color-primary)}
[data-g-status="secondary"]{--g-tint:var(--color-secondary)}
[data-g-status="accent"]{--g-tint:var(--color-accent)}
[data-g-status="info"]{--g-tint:var(--color-info)}
[data-g-status="success"]{--g-tint:var(--color-success)}
[data-g-status="warning"]{--g-tint:var(--color-warning)}
[data-g-status="error"]{--g-tint:var(--color-error)}
[data-g-status="neutral"]{--g-tint:var(--color-neutral)}
@media (hover:hover){
[data-g-status]:hover{background-color:color-mix(in oklab,var(--g-tint) 20%,transparent)}
.g-row-hover:hover{background-color:var(--color-base-200)}
}
[data-g-selected]{background-color:color-mix(in oklab,var(--color-primary) 14%,transparent);box-shadow:inset 3px 0 0 0 var(--color-primary)}
@media (hover:hover){[data-g-selected]:hover{background-color:color-mix(in oklab,var(--color-primary) 22%,transparent)}}
@media (hover:hover){
[data-g-actions]{opacity:0;transition:opacity 120ms ease}
tr:hover [data-g-actions],tr:focus-within [data-g-actions],[data-g-row]:hover [data-g-actions],[data-g-row]:focus-within [data-g-actions],[data-g-actions]:focus-within{opacity:1}
}
[data-align="right"]{text-align:right}
[data-align="center"]{text-align:center}
[data-truncate]{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
g-table tfoot td{font-weight:600;border-top:1px solid var(--color-base-300)}
g-table td[data-g-edit]{cursor:text}
g-table td[data-g-edit="boolean"],g-table td[data-g-edit="select"]{cursor:pointer}
@media (hover:hover){g-table td[data-g-edit]:hover{box-shadow:inset 0 0 0 1px color-mix(in oklab,var(--color-base-content) 22%,transparent)}}
g-table td[data-g-edit]:focus-visible{outline:2px solid color-mix(in oklab,var(--color-primary) 60%,transparent);outline-offset:-2px}
g-table td[data-g-saving]{opacity:.55}
g-table td[data-g-cell-error],g-table td[data-g-cell-error]:hover{box-shadow:inset 0 0 0 1px var(--color-error)}
@keyframes g-cell-flash{from{background-color:var(--g-flash-color,color-mix(in oklab,var(--color-success) 30%,transparent))}to{background-color:transparent}}
g-table td[data-g-flash]{animation:g-cell-flash 1600ms ease-out both}
@keyframes g-input-flash{from{background-color:color-mix(in oklab,var(--color-success) 30%,var(--color-base-100))}to{background-color:var(--color-base-100)}}
g-table td[data-g-flash]>[data-g-cell-input]{animation:g-input-flash 1600ms ease-out both;animation-delay:inherit}
g-table td[data-g-editing]{position:relative}
g-table [data-g-cell-ghost]{visibility:hidden}
g-table td[data-g-editing]>[data-g-cell-input]{position:absolute;inset:0;width:100%;height:100%;min-width:0;min-height:0;padding-block:0;padding-inline:inherit;z-index:1}
g-table td[data-g-editing]>textarea[data-g-cell-input]{bottom:auto;height:auto;padding-block:.25rem;z-index:5}
g-table [data-g-cell-err]{position:absolute;left:0;top:100%;z-index:5;min-width:100%;padding:.125rem .5rem;background:var(--color-base-100);border:1px solid var(--color-error);border-radius:var(--radius-field,.25rem)}
g-table [data-g-cell-note]{border-color:var(--color-info)}
g-table [data-align="right"] [data-g-cell-input]{text-align:right}
@media (max-width:639px){[data-hide-below="sm"]{display:none}}
@media (max-width:767px){[data-hide-below="md"]{display:none}}
@media (max-width:1023px){[data-hide-below="lg"]{display:none}}`;

/**
 * Inject the tint stylesheet once per document.
 *
 * Keyed on the DOM, not a module flag: rollup emits one IIFE per src/
 * directory, so g-table and g-task-list each carry their own private copy of
 * this module. A module-scoped boolean would inject once per loaded bundle.
 */
export function ensureStatusStyles() {
  ensureHueStyles();
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = TINT_CSS;
  document.head.appendChild(style);
}

let warned = false;

/** Any value → a known token, or null. Unknown input fails closed. */
export function normalizeStatus(v) {
  return typeof v === 'string' && STATUS_TOKENS.has(v) ? v : null;
}

/**
 * Resolve a record to a status token, or null for "no status".
 *
 * Returns null rather than falling back to 'neutral' (which g-card-grid's badge
 * resolver does): at row level "no status" must mean *no tint*, not a grey wash
 * on every row.
 *
 * @param {object} record  the host's raw record
 * @param {number} index   row index, passed through to a callback spec
 * @param {function|object|null} spec  callback, or { field?, colorMap?, colorRule? }
 */
export function resolveStatus(record, index, spec) {
  if (!spec || record == null) return null;

  if (typeof spec === 'function') {
    // A host callback must never take the render loop down with it.
    try {
      return normalizeStatus(spec(record, index));
    } catch (err) {
      if (!warned) {
        warned = true;
        console.warn('[gainable] a row-status callback threw; rows render untinted.', err);
      }
      return null;
    }
  }

  if (typeof spec !== 'object') return null;

  const hasField = typeof spec.field === 'string' && spec.field !== '';
  const value = hasField ? record[spec.field] : undefined;

  if (spec.colorMap && typeof spec.colorMap === 'object' && hasField) {
    const mapped = normalizeStatus(spec.colorMap[value]);
    if (mapped) return mapped;
  }

  const rules = Array.isArray(spec.colorRule) ? spec.colorRule
    : Array.isArray(spec.rules) ? spec.rules
      : null;
  if (rules) {
    for (const rule of rules) {
      if (!rule) continue;
      const status = normalizeStatus(rule.status || rule.color);
      if (!status) continue;
      if (!rule.when) return status; // no condition = catch-all
      const ok = hasField ? evalPredicate(value, rule.when) : evalRecord(record, rule.when);
      if (ok) return status;
    }
  }

  return null;
}

/**
 * Token → the attribute fragment that drives the tint, ready to interpolate
 * into an innerHTML template. Empty string when there is no status.
 *
 * The attribute doubles as the accessibility/testing hook: colour alone should
 * not carry meaning, so hosts can target `[data-g-status="error"]` directly
 * rather than parsing class names.
 */
export function statusAttr(status) {
  const s = normalizeStatus(status);
  return s ? ` data-g-status="${s}"` : '';
}

/** Class that gives an UNTINTED row the theme's normal hover background. */
export const ROW_HOVER_CLASS = 'g-row-hover';
